import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MohBalance, PayappItem } from 'src/app/shared/models';
import { of, Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Ability, subject } from '@casl/ability';
import * as moment from 'moment';
import * as fromRoot from 'src/app/state/app.state';
import { projectDetails, selectPayappItems, selectPayapps, selectBillingSettings, selectPayAppTemplates, selectPayAppTemplatesLoading } from 'src/app/modules/projects/core/projects.selectors';
import {
  AddContractorStatusRequest,
  AddContractorStatusSuccess,
  DeletePayappRequest,
  DeletePayappSuccess,
  FetchPayappDetailsRequest,
  FetchPayappDetailsSuccess,
  FetchPayappItemsRequest,
  FetchPayappsRequest,
  FetchBillingSettingsRequest,
  FetchPayAppTemplatesRequest,
  GeneratePayappRequest,
  UpdatePayappStatusRequest,
  UpdatePayappStatusSuccess,
} from 'src/app/modules/projects/core/projects.actions';
import { AuthService } from 'src/app/shared/services';
import { CommonService } from 'src/app/shared/services/common.service';
import { FormControl } from '@angular/forms';
import { CreatePayappDialogComponent, CreatePayAppDialogResult, CreatePayAppDialogData } from '../../dialogs/create-payapp-dialog/create-payapp-dialog.component';
import { PayappCommentsDialogComponent } from '../../dialogs/payapp-comments-dialog/payapp-comments-dialog.component';
import { AddReasonComponent } from '../../dialogs/add-reason/add-reason.component';
import { MohWarningAckDialogComponent } from '../../dialogs/moh-warning-ack-dialog/moh-warning-ack-dialog.component';
import { PayappDateFilterComponent } from '../../filters/payapp-date-filter/payapp-date-filter.component';
import { PayappMultiFilterComponent } from '../../filters/payapp-multi-filter/payapp-multi-filter.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { ProjectsService } from '../../../../../core/projects.service';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';

@Component({
    selector: 'app-pay-app',
    templateUrl: './pay-app.component.html',
    styleUrls: ['./pay-app.component.scss'],
    standalone: false
})
export class PayappComponent implements OnInit, OnDestroy {
  public projectDetails: any;
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('dateFilter') dateFilterComponent: PayappDateFilterComponent;
  @ViewChild('multiFilter') multiFilterComponent: PayappMultiFilterComponent;
  @ViewChild('searchInputFull') searchInputFull: ElementRef;
  @ViewChild('payappNameEl') payappNameEl: ElementRef;
  public isPayappNameTruncated = false;
  public loading = 0;
  public downloading = false;
  public downloadingMohLedger = false;
  public initialized = false;
  @Input()

  payappItems: PayappItem[] = [];
  payapps: any [];
  defaultDateValue = 'All time';
  selectedPayapp: any = null; // Default selection
  unpaidPayapp = { id: 'unpaid', name: 'Unpaid', start_date: null, end_date: null };
  allUnpaidItems: PayappItem[] = [];
  includedPriorItems = new Set<string>();
  quantityDrafts: Record<string, { quantity: number; comments: string; status: string }> = {};
  payableQuantityMap: Record<string, number> = {};
  showOutstanding = false;
  showDeferredItems = false;

  deferredQuantity(item: PayappItem): number {
    const draft = this.quantityDrafts[item.report_bid_item_id];
    const status = draft?.status ?? item.pay_app_status ?? 'ready';
    if (status === 'rejected') return 0;
    const payable = status === 'deferred' ? 0 : Number(draft?.quantity ?? item.pay_app_quantity ?? item.quantity);
    return Math.max(0, Math.round((Number(item.quantity) - payable) * 1e8) / 1e8);
  }

  statusSelectionValue(item: PayappItem): string {
    const status = this.tempStatusMap[item.report_bid_item_id] || this.statusMap[item.report_bid_item_id];
    return status === 'ready' && this.deferredQuantity(item) > 0 ? 'partially-deferred' : status;
  }

  get deferredItems(): PayappItem[] {
    return this.allUnpaidItems.filter(item => this.deferredQuantity(item) > 0);
  }

  toggleDeferredItems(): void {
    this.showDeferredItems = !this.showDeferredItems;
    if (this.showDeferredItems) this.searchControl.setValue('');
    this.showOutstanding = false;
    this.showPartialPaymentReview = false;
    this.pagination.pageIndex = 0;
    this.filterDisplayedItems();
  }

  isOutsidePaymentSelection(item: PayappItem): boolean {
    return (this.showOutstanding || this.showDeferredItems)
      && !this.payappItems.some(selected => selected.report_bid_item_id === item.report_bid_item_id);
  }

  showQuantityBreakdown(item: PayappItem): boolean {
    if (this.readOnly || this.isContractorMode) return false;
    if (this.isOutsidePaymentSelection(item)) {
      return !!item.paid_quantity || (this.deferredQuantity(item) > 0 && this.deferredQuantity(item) < Number(item.quantity));
    }
    return Number(item.reported_quantity ?? item.quantity) !== this.contractPayableQuantity(item);
  }

  toggleOutstanding(): void {
    this.showOutstanding = !this.showOutstanding;
    this.showDeferredItems = false;
    this.showPartialPaymentReview = false;
    this.pagination.pageIndex = 0;
    this.filterDisplayedItems();
  }

  get priorPeriodItems(): PayappItem[] {
    if (!this.startDate) return [];
    return this.allUnpaidItems.filter(item => moment(item.report_date).isBefore(this.startDate, 'day')
      && item.pay_app_status !== 'rejected');
  }

  get priorPeriodAmount(): number {
    return this.priorPeriodItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  }

  includePriorItem(item: PayappItem): void {
    const id = item.report_bid_item_id;
    delete this.tempStatusMap[id];
    if (this.includedPriorItems.has(id)) {
      this.includedPriorItems.delete(id);
    } else {
      this.includedPriorItems.add(id);
      const draft = this.quantityDrafts[id];
      this.quantityDrafts[id] = {
        quantity: (draft?.status ?? item.pay_app_status) === 'deferred'
          ? Number(item.quantity) : Number(draft?.quantity ?? item.pay_app_quantity ?? item.quantity),
        comments: draft?.comments ?? item.pay_app_comments ?? '', status: 'ready',
      };
    }
    this.resetPayappItems();
  }

  payableQuantity(item: PayappItem): number {
    return this.payableQuantityMap[item.report_bid_item_id] ?? Number(item.quantity);
  }

  private contractOverages = new Map<number, number>();
  private contractPayableQuantities = new Map<string, number>();
  hasContractOverage = false;
  showPartialPaymentReview = false;
  partialPaymentReviewCount = 0;
  fullDeferralReviewCount = 0;

  get deferralReviewMessage(): string {
    const full = this.fullDeferralReviewCount;
    const partial = this.partialPaymentReviewCount - full;
    if (!this.partialPaymentReviewCount) return 'All deferrals reviewed';
    if (!partial) return `${full} ${full === 1 ? 'item must' : 'items must'} be deferred in full · Reason required`;
    if (!full) return `${partial} ${partial === 1 ? 'item needs' : 'items need'} a partial payment reason`;
    return `${partial} ${partial === 1 ? 'partial payment' : 'partial payments'} and ${full} ${full === 1 ? 'full deferral' : 'full deferrals'} need a reason`;
  }

  get deferralReviewAction(): string {
    const label = this.fullDeferralReviewCount === this.partialPaymentReviewCount ? 'Review full deferrals'
      : this.fullDeferralReviewCount ? 'Review deferrals' : 'Review partial payments';
    return `${label} (${this.partialPaymentReviewCount})`;
  }

  contractPayableQuantity(item: PayappItem): number {
    return this.contractPayableQuantities.get(item.report_bid_item_id) ?? this.payableQuantity(item);
  }

  needsPartialPaymentReview(item: PayappItem): boolean {
    return this.statusMap[item.report_bid_item_id] === 'ready'
      && this.payableQuantity(item) - this.contractPayableQuantity(item) > 0.000000005;
  }

  togglePartialPaymentReview(): void {
    this.showPartialPaymentReview = !this.showPartialPaymentReview;
    this.showOutstanding = false;
    this.showDeferredItems = false;
    this.pagination.pageIndex = 0;
    this.filterDisplayedItems();
  }

  contractOverage(item: PayappItem): number {
    return this.contractOverages.get(item.bid_item_key) || 0;
  }

  editPayableQuantity(item: PayappItem): void {
    const id = item.report_bid_item_id;
    const other = this.payappItems.filter(row => row.bid_item_key === item.bid_item_key
      && row.report_bid_item_id !== id && this.statusMap[row.report_bid_item_id] === 'ready')
      .reduce((sum, row) => sum + this.payableQuantity(row), 0);
    const maxPayable = Math.max(this.contractPayableQuantity(item), 0, Math.min(Number(item.quantity),
      Math.round((Number(item.contract_remaining || 0) - other) * 1e8) / 1e8));
    this.dialog.open(PayappCommentsDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { item: item.item, remaining: Number(item.quantity), maxPayable,
        quantity: Math.min(this.payableQuantity(item), maxPayable), comments: this.commentsMap[id] || '' },
    }).afterClosed().pipe(take(1)).subscribe(result => {
      if (!result) return;
      const status = result.quantity > 0 ? 'ready' : 'deferred';
      this.projectsService.updatePayappStatus({ dailyReportId: item.daily_report_id,
        reportBidItemId: id, data: { status, comments: result.comments, quantity: result.quantity },
      }).pipe(take(1)).subscribe({
        next: (response) => {
          this.store.dispatch(UpdatePayappStatusSuccess({ data: response.data }));
          this.quantityDrafts[id] = { ...result, status };
          this.payableQuantityMap[id] = result.quantity;
          this.statusMap[id] = status;
          delete this.tempStatusMap[id];
          this.commentsMap[id] = result.comments;
          this.calculateTotalAmount();
        },
        error: () => this.commonService.notification('Unable to save quantity allocation. Refresh and try again.', 'error'),
      });
    });
  }

  contractorStatusList = [
    { key: 1, value: 'Agree' },
    { key: 0, value: 'Disagree' },
  ];
  public displayedItems: PayappItem[] = [];
  public totalRecords: number;
  public totalAmount = 0;
  public statusMap: { [report_bid_item_id: string]: string } = {};
  public tempStatusMap: { [report_bid_item_id: string]: string } = {};
  public commentsMap: { [report_bid_item_id: string]: string } = {};
  public contractorStatusMap: { [report_bid_item_id: string]: string } = {};
  public tempContractorStatusMap: { [report_bid_item_id: string]: string } = {};
  public contractorCommentsMap: { [report_bid_item_id: string]: string } = {};
  public contractorSuggestedQuantityMap: { [report_bid_item_id: string]: number } = {};
  public searchControl = new FormControl('');
  public pagination = {
    pageIndex: 0,
    pageSize: 10,
    length: 0,
  };
  currentUser: any;
  // Define the displayedColumns array
  displayedColumns: string[] = ['Date', 'Site', 'Created By', 'Bid Item', 'Quantity', 'Amount', 'Status'];
  sortByColumns: string[] = ['Date', 'Site', 'Bid Item', 'Status', 'Quantity', 'Amount', 'Created By'];

  // G703 columns for submitted pay apps
  get g703Columns(): string[] {
    const cols = ['Item', 'Description', 'Unit', 'OriginalQty', 'COQty', 'AdjustedQty', 'QtyThisPeriod', 'QtyPrevious', 'QtyToDate', 'PercentComplete', 'UnitPrice', 'G703Amount'];
    if (this.hasMohLedger) {
      cols.splice(cols.indexOf('G703Amount'), 0, 'MaterialsStored', 'StoredDelta', 'StoredCompletedTotal');
    }
    if (!this.includeChangeOrders) {
      return cols.filter(c => c !== 'COQty' && c !== 'AdjustedQty');
    }
    return cols;
  }
  g703SortByColumns: string[] = ['Item', 'This Period', 'To Date', '% Complete', 'Unit Price', 'Amount'];
  showG703View = false;

  // Grouped items for G703 expandable view
  groupedItems: any[] = [];
  displayedGroupedItems: any[] = []; // Paginated slice of groupedItems
  expandedBidItems: Set<string> = new Set();
  isSingleSiteProject = false; // Hide site column when all items are from one site
  columnPropertyMap: { [key: string]: string } = {
    Date: 'report_date',
    Site: 'site_name',
    'Created By': 'created_by',
    'Bid Item': 'item',
    Quantity: 'bid_item_quantity',
    Amount: 'amount',
    Status: 'pay_app_status',
  };
  payappItemsMap = new Map<string, PayappItem>();
  startDate = null;
  endDate = null;
  readOnly: boolean;
  canEdit = false;
  filters: any = null;
  selectedSortColumn: string = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  isSearchActive = false;
  isContractorMode = false;
  billingSettings: any = null;
  templates: any[] = [];
  templatesLoading = false;
  payappSummary: any = null; // Calculated summary from bid items
  mohAttachmentUploading: Record<string, boolean> = {};
  summaryExpanded: boolean = true; // Default expanded, persisted to localStorage
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    public dialog: MatDialog,
    private authService: AuthService,
    private actions: Actions,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private snackBar: MatSnackBar,
    @Inject(Ability) private ability: Ability,
    private analyticsService: AnalyticsService,
  ) {
    this.currentUser = this.authService?.getCurrentUser() || {};
  }

  get hasReadyItems(): boolean {
    return this.payappItems && this.payappItems.some(item => this.statusMap[item.report_bid_item_id] === 'ready');
  }

  get useLegacyLayout(): boolean {
    return this.billingSettings?.use_legacy_payapp_layout === true;
  }

  get hasExcelDownload(): boolean {
    return !this.useLegacyLayout;
  }

  get includeChangeOrders(): boolean {
    return this.billingSettings?.include_change_orders !== false;
  }

  get hasMohLedger(): boolean {
    return this.selectedPayapp?.calculated?.has_moh_ledger === true
      || this.selectedPayapp?.has_moh_ledger === true
      || this.payappItems?.some(item => !!item.moh);
  }

  retainagePercentage: number = 0;

  get hasAdjustments(): boolean {
    if (!this.selectedPayapp || this.selectedPayapp.id === 'unpaid') return false;
    return !!(
      this.retainagePercentage > 0 ||
      this.selectedPayapp.retainage_released ||
      this.selectedPayapp.material_on_hand ||
      this.selectedPayapp.liquidated_damages_days
    );
  }

  ngOnInit(): void {
    // Load summary accordion preference from localStorage
    const savedExpanded = localStorage.getItem('payapp-summary-expanded');
    if (savedExpanded !== null) {
      this.summaryExpanded = savedExpanded === 'true';
    }

    this.route.queryParams.pipe(
      filter(params => params.tab === 'payapp' && !this.initialized),
      switchMap(() => this.store.select(projectDetails).pipe(
        tap(data => {
          if (Object.keys(data).length !== 0) {
            this.projectDetails = data;
            this.canEdit = this.ability.can('update', subject('Project', { id: data.id, uuid: data.uuid }));
            if (!this.selectedPayapp) {
              this.selectedPayapp = this.unpaidPayapp;
            }
            // Set contractor mode
            // tslint:disable-next-line:max-line-length
            this.isContractorMode = this.currentUser.roles.some(r => r.project_id === this.projectDetails?.id && (r.role === 'contractor-pm' || r.role === 'contractor-inspector'));
            this.onPayappChange(this.selectedPayapp);
            this.fetchPayappItems(this.selectedPayapp.id);
            this.fetchPayapps();
            this.fetchBillingSettings();
            this.fetchTemplates();
          }
        }),
      )),
      takeUntil(this.onDestroy),
    ).subscribe(() => {
      // Subscribe to selectPayappItems
      this.store.select(selectPayappItems).pipe(
        filter((items: any) => !!items && Object.keys(items).length > 0),
        takeUntil(this.onDestroy),
      ).subscribe((items: any) => {
        const prevPayappItems = this.payappItems;
        this.allUnpaidItems = items.unpaid || [];
        this.payappItems = items[this.selectedPayapp?.id] || [];

        this.payappItems = this.applyFilters(this.payappItems);
        this.applyFilters(prevPayappItems);
        const resetPagination = !(this.payappItems && this.payappItems.length === prevPayappItems?.length);

        this.initializePayappItems(resetPagination);
        this.initialized = true;
      });

      // Subscribe to selectPayapps
      this.store.select(selectPayapps).pipe(
        takeUntil(this.onDestroy),
      ).subscribe((payapps: any[]) => {
        this.payapps = [this.unpaidPayapp, ...payapps];
      });
    });

    // Search control value changes subscription
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.onDestroy),
      )
      .subscribe(() => {
        this.filterDisplayedItems();
      });

    // Switch to new payapp after save and update with full details
    this.actions.pipe(
      ofType(FetchPayappDetailsSuccess),
      takeUntil(this.onDestroy),
    ).subscribe((action: any) => {
      if (action.type === FetchPayappDetailsSuccess.type) {
        // Use payappDetails from action if available (contains full payapp data)
        if (action.payappDetails) {
          this.selectedPayapp = action.payappDetails;
          // Update the payapp in local array too
          const idx = this.payapps.findIndex(p => p.id === action.payappId);
          if (idx >= 0) {
            this.payapps[idx] = { ...this.payapps[idx], ...action.payappDetails };
          }
        } else {
          const generatedPayapp = this.payapps.find(payapp => payapp.id === action.payappId);
          if (generatedPayapp) {
            this.selectedPayapp = generatedPayapp;
          }
        }
        this.onPayappChange(this.selectedPayapp);
        this.resetFilters();
      }
    });

    this.actions.pipe(
      ofType(AddContractorStatusSuccess),
      takeUntil(this.onDestroy),
    ).subscribe((action) => {
      if (action.type === AddContractorStatusSuccess.type) {
        this.fetchPayappItems(this.selectedPayapp?.id);
        this.initializeMaps();
      }
    });


    // Switch to unpaid after delete
    this.actions.pipe(
      ofType(DeletePayappSuccess),
      takeUntil(this.onDestroy),
    ).subscribe((action) => {
      if (action.type === DeletePayappSuccess.type) {
        this.selectedPayapp = this.unpaidPayapp;
        this.onPayappChange(this.selectedPayapp);
        this.resetFilters();
      }
    });


    this.commonService.closeDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialog.closeAll();
      });

  }

  fetchPayappItems(payappId: string) {
    const qp = {
      includeDeferred: true,
      includeRejected: true,
    };

    if (payappId === 'unpaid') {
      this.store.dispatch(FetchPayappItemsRequest({
        payload: {
          projectId: this.projectDetails?.id,
          qp,
        },
      }));
    } else if (payappId) {
      this.store.dispatch(FetchPayappDetailsRequest({
        payload: {
          projectId: this.projectDetails?.id,
          payappId,
          qp: {
            // Use line_items for G703 data (includes quantity_previous, quantity_to_date, etc.)
            include: ['line_items', 'calculated'],
          },
        },
      }));
    }
    return of(null);
  }

  fetchPayapps() {
    this.store.dispatch(FetchPayappsRequest({
      payload: {
        projectId: this.projectDetails?.id,
        qp: {
          // Include calculated data so amounts show in dropdown
          include: ['calculated'],
        },
      },
    }));
    return of(null);
  }

  fetchBillingSettings(): void {
    if (this.projectDetails?.id) {
      this.store.dispatch(FetchBillingSettingsRequest({
        payload: { projectId: this.projectDetails.id },
      }));

      // Subscribe to billing settings
      this.store.select(selectBillingSettings)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(settings => {
          this.billingSettings = settings;
          this.updateRetainagePercentage();
          // Update G703 view flag when billing settings change (legacy mode check)
          if (this.selectedPayapp) {
            const useLegacyLayout = settings?.use_legacy_payapp_layout === true;
            this.showG703View = this.readOnly && !useLegacyLayout;
          }
        });
    }
  }

  fetchTemplates(): void {
    if (this.projectDetails?.id) {
      this.store.dispatch(FetchPayAppTemplatesRequest({
        projectId: this.projectDetails.id,
      }));

      // Subscribe to templates
      this.store.select(selectPayAppTemplates)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(templates => {
          this.templates = templates || [];
        });

      this.store.select(selectPayAppTemplatesLoading)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(loading => {
          this.templatesLoading = loading;
        });
    }
  }

  initializePayappItems(resetPagination: boolean): void {
    this.initializeMaps();
    if (resetPagination) {
      this.pagination = {
        pageIndex: 0,
        pageSize: 10,
        length: this.payappItems?.length,
      };
    }

    // Populate missing G703 fields for display
    this.populateG703Fields();

    this.payappItems = this.sortItems(this.payappItems);
    this.calculateTotalAmount();
    this.filterDisplayedItems();
    this.calculatePayappSummary(); // Calculate summary from bid items
    this.groupItemsByBidItem(); // Group items for expandable G703 view
  }

  // Populate missing G703 fields (Original Qty, Previous, To Date, % Complete)
  populateG703Fields(): void {
    if (!this.payappItems || this.selectedPayapp?.id === 'unpaid') {
      return;
    }

    // Create new array with cloned items to avoid mutating NgRx store state
    this.payappItems = this.payappItems.map(item => {
      // Clone the item to make it mutable
      const clonedItem = { ...item } as PayappItem;

      // Original Qty - use bid_item_quantity if original_quantity not set
      if (clonedItem.original_quantity == null) {
        clonedItem.original_quantity = clonedItem.bid_item_quantity ?? 0;
      }

      // Change Order Qty - default to 0
      if (clonedItem.change_order_quantity == null) {
        clonedItem.change_order_quantity = 0;
      }

      // Adjusted Qty = Original + CO
      if (clonedItem.adjusted_quantity == null) {
        clonedItem.adjusted_quantity = (clonedItem.original_quantity || 0) + (clonedItem.change_order_quantity || 0);
      }

      // Previous = quantity_previous or 0
      if (clonedItem.quantity_previous == null) {
        clonedItem.quantity_previous = 0;
      }

      // To Date = This Period + Previous
      if (clonedItem.quantity_to_date == null) {
        clonedItem.quantity_to_date = (clonedItem.quantity || 0) + (clonedItem.quantity_previous || 0);
      }

      // % Complete = (To Date / Adjusted) * 100
      if (clonedItem.percent_complete == null) {
        const adjustedQty = clonedItem.adjusted_quantity || 0;
        clonedItem.percent_complete = adjustedQty > 0 ? (clonedItem.quantity_to_date / adjustedQty) * 100 : 0;
      }

      return clonedItem;
    });
  }

  // ON change sort column
  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.filterDisplayedItems();
  }

  // ON toggle sort direction
  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.filterDisplayedItems();
  }

  sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const itemsToSort = [...items];

    // Default sort functions for each column
    const sortFns = {
      Date: (a, b) => (a.report_date || '').localeCompare(b.report_date || ''),
      Site: (a, b) => (a.site_name || '').localeCompare(b.site_name || ''),
      'Created By': (a, b) => (a.created_by_name || a.created_by || '').localeCompare(b.created_by_name || b.created_by || ''),
      'Bid Item': (a, b) => (a.item || '').localeCompare(b.item || ''),
      Quantity: (a, b) => a.quantity - b.quantity,
      Amount: (a, b) => (a.quantity * a.unit_price) - (b.quantity * b.unit_price),
      Status: (a, b) => {
        const statusA = this.isContractorMode ? (a.contractor_status || 'agree') : (a.pay_app_status || 'ready');
        const statusB = this.isContractorMode ? (b.contractor_status || 'agree') : (b.pay_app_status || 'ready');
        return statusA.localeCompare(statusB);
      },
    };

    // Handle sort direction for selected column
    const applySortDirection = (result) => {
      return this.sortDirection === 'asc' ? result : -result;
    };

    // First, sort by the selected column if any
    if (this.selectedSortColumn && sortFns[this.selectedSortColumn]) {
      itemsToSort.sort((a, b) => {
        const result = sortFns[this.selectedSortColumn](a, b);
        if (result !== 0) {
          return applySortDirection(result);
        } // Only apply selected column sort direction if values are different

        // tslint:disable-next-line:max-line-length
        const defaultSortOrder = ['Date', 'Site', 'Bid Item', 'Quantity', 'Amount', 'Status'].filter(col => col !== this.selectedSortColumn);

        for (const col of defaultSortOrder) {
          const secondaryResult = sortFns[col](a, b);
          if (secondaryResult !== 0) {
            return secondaryResult;
          }
        }
        return 0;
      });
    } else {
      // If no selected column, just apply default sorting
      const defaultSortOrder = ['Date', 'Site', 'Bid Item', 'Quantity', 'Amount', 'Status'];

      itemsToSort.sort((a, b) => {
        for (const col of defaultSortOrder) {
          const result = sortFns[col](a, b);
          if (result !== 0) {
            return result;
          } // Return as soon as a default sort has different values
        }
        return 0;
      });
    }
    return itemsToSort;
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.filterDisplayedItems();
  }

  onSearchClick(event: Event): void {
    event?.stopPropagation();
    this.isSearchActive = true;  // Activates full search mode
    setTimeout(() => this.searchInputFull?.nativeElement.focus(), 0);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.isSearchActive = false;
    setTimeout(() => this.searchInput?.nativeElement.blur(), 0);
  }


  // Initialize maps
  initializeMaps(): void {
    this.payappItems?.forEach(item => {
      this.payappItemsMap.set(item.report_bid_item_id, item);
      const draft = this.quantityDrafts[item.report_bid_item_id];
      this.payableQuantityMap[item.report_bid_item_id] = draft?.quantity ?? Number(item.pay_app_quantity ?? item.quantity);
      this.statusMap[item.report_bid_item_id] = draft?.status || item.pay_app_status || 'ready';
      this.commentsMap[item.report_bid_item_id] = draft?.comments ?? item.pay_app_comments ?? '';
      this.contractorStatusMap[item.report_bid_item_id] = item.contractor_status || 'agree';
      this.contractorCommentsMap[item.report_bid_item_id] = item.contractor_comments || '';
      this.contractorSuggestedQuantityMap[item.report_bid_item_id] = item.contractor_suggested_quantity || null;
    });
  }

  // Calculate summary values from payapp items (bid items) for submitted payapps
  calculatePayappSummary(): void {
    if (!this.payappItems || this.payappItems.length === 0 || this.selectedPayapp?.id === 'unpaid') {
      this.payappSummary = null;
      return;
    }

    // Use backend calculated values if available (preferred - handles duplicates correctly)
    const backendCalculated = this.selectedPayapp?.calculated;

    // Calculate work completed THIS PERIOD from line items (sum of quantity * unit_price)
    let workCompletedThisPeriod = 0;
    this.payappItems.forEach(item => {
      const unitPrice = item.unit_price || 0;
      const qtyThisPeriod = item.quantity || 0;
      workCompletedThisPeriod += qtyThisPeriod * unitPrice;
    });

    // Use backend values for cumulative amounts (avoids overcounting when same bid_item appears multiple times)
    const originalContractAmount = backendCalculated?.original_contract_amount || 0;
    const changeOrdersToDate = backendCalculated?.total_change_orders || 0;
    // Work completed to date must come from backend (sums across all pay apps)
    const workCompletedToDate = backendCalculated?.work_completed_to_date || workCompletedThisPeriod;

    const totalContractAmount = originalContractAmount + changeOrdersToDate;
    const materialOnHand = this.selectedPayapp?.material_on_hand || 0;
    const totalEarnings = workCompletedToDate + materialOnHand;

    // Retainage calculations
    // Use ?? to respect 0% retainage on old pay apps (0 is falsy with ||)
    const retainagePercent = this.selectedPayapp?.retainage_percentage ?? this.billingSettings?.retainage_percentage ?? 5;
    const retainageAmount = totalEarnings * (retainagePercent / 100);
    const retainageReleased = this.selectedPayapp?.retainage_released || 0;
    const netRetainage = retainageAmount - retainageReleased;

    // Liquidated damages
    const ldDays = this.selectedPayapp?.liquidated_damages_days || 0;
    const ldRate = this.selectedPayapp?.liquidated_damages_rate || this.billingSettings?.liquidated_damages_per_day || 0;
    const ldAmount = ldDays * ldRate;

    const totalDeductions = netRetainage + ldAmount;
    const previousPayments = this.selectedPayapp?.previous_payments || 0;
    const amountDueThisPeriod = totalEarnings - totalDeductions - previousPayments;

    this.payappSummary = {
      original_contract_amount: originalContractAmount,
      total_change_orders: changeOrdersToDate,
      material_on_hand: backendCalculated?.material_on_hand ?? materialOnHand,
      previous_payments: previousPayments,
      days_charged_to_date: this.selectedPayapp?.days_charged_to_date || 0,
      percent_complete_time: this.selectedPayapp?.percent_complete_time || 0,
      percent_complete_place: totalContractAmount > 0 ? (workCompletedToDate / totalContractAmount) * 100 : 0,
      calculated: backendCalculated || {
        original_contract_amount: originalContractAmount,
        total_change_orders: changeOrdersToDate,
        total_contract_amount: totalContractAmount,
        work_completed_to_date: workCompletedToDate,
        total_earnings: totalEarnings,
        retainage_amount: retainageAmount,
        net_retainage: netRetainage,
        liquidated_damages_amount: ldAmount,
        total_deductions: totalDeductions,
        amount_due_this_period: amountDueThisPeriod,
      },
    };
  }

  // Group items by bid_item_id for expandable G703 view
  groupItemsByBidItem(): void {
    if (!this.showG703View || !this.payappItems?.length) {
      this.groupedItems = [];
      this.displayedGroupedItems = [];
      return;
    }

    const groups = new Map<string, any>();
    const allSites = new Set<string>();

    // Group ALL items (not just displayed/paginated items)
    for (const item of this.payappItems) {
      if (item.site_name) allSites.add(item.site_name);
      const key = item.item; // Use item code as key

      if (!groups.has(key)) {
        groups.set(key, {
          item: item.item,
          description: item.description,
          uom: item.uom,
          unit_price: item.unit_price,
          original_quantity: item.original_quantity || 0,
          change_order_quantity: item.change_order_quantity || 0,
          adjusted_quantity: item.adjusted_quantity || 0,
          quantity_previous: item.quantity_previous || 0,
          // Aggregated values
          quantity: 0,
          amount: 0,
          quantity_to_date: 0,
          percent_complete: 0,
          latestDate: null, // Most recent report_date for sorting
          // Detail items
          details: [],
          // Counts for summary display
          siteCount: new Set<string>(),
          creatorCount: new Set<string>(),
          moh: item.moh || null,
        });
      }

      const group = groups.get(key);
      group.quantity += item.quantity || 0;
      group.amount += (item.quantity || 0) * (item.unit_price || 0);
      group.details.push(item);
      if (item.site_name) group.siteCount.add(item.site_name);
      if (item.created_by_name) group.creatorCount.add(item.created_by_name);
      if (!group.moh && item.moh) {
        group.moh = item.moh;
      }
      // Track most recent date
      if (item.report_date && (!group.latestDate || item.report_date > group.latestDate)) {
        group.latestDate = item.report_date;
      }
    }

    // Detect single-site project to hide site column
    this.isSingleSiteProject = allSites.size <= 1;

    // Calculate to_date and percent_complete for each group
    this.groupedItems = Array.from(groups.values()).map(group => {
      group.quantity_to_date = group.quantity_previous + group.quantity;
      group.percent_complete = group.adjusted_quantity > 0
        ? (group.quantity_to_date / group.adjusted_quantity) * 100
        : 0;
      if (group.moh) {
        group.moh = {
          ...group.moh,
          E: group.amount,
          G: (group.moh.D || 0) + group.amount + (group.moh.F || 0),
        };
      }
      group.siteCountNum = group.siteCount.size;
      group.creatorCountNum = group.creatorCount.size;
      // Sort detail rows by date descending (most recent first)
      group.details.sort((a, b) => (b.report_date || '').localeCompare(a.report_date || ''));
      return group;
    });

    // Filter groups by search term
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    if (searchTerm) {
      this.groupedItems = this.groupedItems.filter(group => {
        // Check group-level fields
        const groupMatch = [group.item, group.description, group.uom, String(group.unit_price)]
          .some(val => String(val || '').toLowerCase().includes(searchTerm));
        if (groupMatch) return true;
        // Check detail-level fields
        return group.details.some(detail =>
          [detail.site_name, detail.created_by_name, moment(detail.report_date).format('MMM D, YYYY')]
            .some(val => String(val || '').toLowerCase().includes(searchTerm))
        );
      });
    }

    // Sort grouped items
    this.sortGroupedItems();

    // Update pagination length based on groups count
    this.pagination.length = this.groupedItems.length;

    // Create paginated slice
    this.displayedGroupedItems = this.groupedItems.slice(
      this.pagination.pageIndex * this.pagination.pageSize,
      (this.pagination.pageIndex + 1) * this.pagination.pageSize
    );
  }

  // Sort grouped items based on selected sort column
  sortGroupedItems(): void {
    if (!this.groupedItems?.length) {
      return;
    }

    const direction = this.sortDirection === 'asc' ? 1 : -1;

    const sortFns: { [key: string]: (a: any, b: any) => number } = {
      Item: (a, b) => (a.item || '').localeCompare(b.item || ''),
      'This Period': (a, b) => (a.quantity || 0) - (b.quantity || 0),
      'To Date': (a, b) => (a.quantity_to_date || 0) - (b.quantity_to_date || 0),
      '% Complete': (a, b) => (a.percent_complete || 0) - (b.percent_complete || 0),
      'Unit Price': (a, b) => (a.unit_price || 0) - (b.unit_price || 0),
      Amount: (a, b) => (a.amount || 0) - (b.amount || 0),
    };

    if (this.selectedSortColumn && sortFns[this.selectedSortColumn]) {
      this.groupedItems.sort((a, b) => {
        const result = sortFns[this.selectedSortColumn](a, b);
        return result !== 0 ? result * direction : (a.item || '').localeCompare(b.item || '');
      });
    } else {
      // Default sort by item code
      this.groupedItems.sort((a, b) => (a.item || '').localeCompare(b.item || ''));
    }
  }

  // Handle clicking on G703 column header to sort
  sortOnColumn(column: string): void {
    if (this.selectedSortColumn === column) {
      this.toggleSortDirection();
    } else {
      this.selectedSortColumn = column;
      this.sortDirection = 'asc';
      this.filterDisplayedItems();
    }
  }

  toggleBidItemExpand(itemCode: string): void {
    if (this.expandedBidItems.has(itemCode)) {
      this.expandedBidItems.delete(itemCode);
    } else {
      this.expandedBidItems.add(itemCode);
    }
  }

  isBidItemExpanded(itemCode: string): boolean {
    return this.expandedBidItems.has(itemCode);
  }

  isEllipsisActive(el: HTMLElement): boolean {
    return el ? el.scrollWidth > el.clientWidth : false;
  }

  updateRetainagePercentage(): void {
    this.retainagePercentage = this.selectedPayapp?.retainage_percentage ?? this.billingSettings?.retainage_percentage ?? 5;
  }

  resetFilters(): void {
    if (this.dateFilterComponent) {
      this.dateFilterComponent.removeDateFilter(null);
    }

    if (this.multiFilterComponent) {
      this.multiFilterComponent.resetFilters();
    }

    // reset search filter
  }

  onStatusChange(reportBidItemId: string, newStatus: string): void {
    this.analyticsService.capture('payapp_action', {
      action: 'item_status_changed',
      project_id: this.projectDetails?.id,
      status: newStatus,
    });

    // Check if the status change requires a comment
    if (newStatus === 'deferred' || newStatus === 'rejected') {
      const previousStatus = this.statusMap[reportBidItemId];
      this.tempStatusMap[reportBidItemId] = newStatus;
      this.openCommentsDialog(reportBidItemId, previousStatus);
    } else {
      // Directly update status if no dialog is needed
      this.statusMap[reportBidItemId] = newStatus;
      this.tempStatusMap[reportBidItemId] = newStatus;
      delete this.quantityDrafts[reportBidItemId];
      this.commentsMap[reportBidItemId] = '';
      const item = this.payappItems.find(row => row.report_bid_item_id === reportBidItemId);
      this.payableQuantityMap[reportBidItemId] = Number(item?.quantity || 0);
      this.updatePayappStatus(reportBidItemId);
      this.calculateTotalAmount();
    }
  }

  onContractorStatusChange(reportBidItemId: string, newStatus: string): void {
    // Check if the status change requires a comment
    if (newStatus === 'disagree') {
      const previousContractorStatus = this.contractorStatusMap[reportBidItemId];
      this.tempContractorStatusMap[reportBidItemId] = newStatus;
      this.openContractorStatusDialog(reportBidItemId, previousContractorStatus);
    } else {
      // Directly update status if no dialog is needed
      this.contractorStatusMap[reportBidItemId] = newStatus;
      this.tempContractorStatusMap[reportBidItemId] = newStatus;
      this.contractorCommentsMap[reportBidItemId] = '';
      this.contractorSuggestedQuantityMap[reportBidItemId] = null;
      this.updateContractorStatusToAgreed(reportBidItemId);
    }
  }

  openCommentsDialog(reportBidItemId: string, previousStatus: string): void {
    const dialogRef = this.dialog.open(PayappCommentsDialogComponent, {
      data: { comments: this.commentsMap[reportBidItemId] || '' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        delete this.quantityDrafts[reportBidItemId];
        this.commentsMap[reportBidItemId] = result;
        this.statusMap[reportBidItemId] = this.tempStatusMap[reportBidItemId]; // Commit the status change
        this.calculateTotalAmount();
        // Dispatch the action to update status and comments
        this.updatePayappStatus(reportBidItemId);
      } else {
        delete this.tempStatusMap[reportBidItemId]; // Discard the temporary status
        // Revert to the previous status
        this.statusMap[reportBidItemId] = previousStatus;
      }
    });
  }

  openContractorStatusDialog(reportBidItemId: string, previousStatus: string): void {
    const selectedItem = this.payappItems.find(item => item.report_bid_item_id === reportBidItemId);

    const data = {
      dailyReportId: selectedItem.daily_report_id,
      id: reportBidItemId,
      contractor_comments: selectedItem.contractor_comments,
      contractor_status: selectedItem.contractor_status,
      contractor_suggested_quantity: selectedItem.contractor_suggested_quantity,
      contractor_pictures: selectedItem.contractor_pictures || [],
      media: selectedItem?.['media'],
    };
    const dialogRef = this.dialog.open(AddReasonComponent, {
      disableClose: false,
      data: { x: 3, selectedRowData: data },
      panelClass: 'floating-dialog-box',
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        this.contractorCommentsMap[reportBidItemId] = result?.comments;
        this.contractorStatusMap[reportBidItemId] = this.tempContractorStatusMap[reportBidItemId]; // Commit the status change
      } else {
        delete this.tempContractorStatusMap[reportBidItemId];
        // Revert to the previous status
        if (previousStatus) {
          this.contractorStatusMap[reportBidItemId] = previousStatus;
        }
      }
    });
  }


  updatePayappStatus(reportBidItemId: string): void {
    // Dispatch the action to update status and comments
    this.store.dispatch(UpdatePayappStatusRequest({
      payload: {
        dailyReportId: this.payappItems.find(item => item.report_bid_item_id === reportBidItemId).daily_report_id,
        reportBidItemId,
        data: {
          status: this.statusMap[reportBidItemId],
          comments: this.commentsMap[reportBidItemId],
        },
      },
    }));
  }

  updateContractorStatusToAgreed(reportBidItemId: string): void {
    const selectedItem = this.payappItems.find(item => item.report_bid_item_id === reportBidItemId);
    const payload = {
      comments: '',
      suggested_quantity: null,
      reportBidItemId,
      dailyReportId: selectedItem.daily_report_id,
      status: 'agree',
    };

    this.store.dispatch(AddContractorStatusRequest({ payload }));

  }

  onDateChanged(dates: { startDate: string | null, endDate: string | null }): void {
    this.showOutstanding = false;
    this.showDeferredItems = false;
    this.includedPriorItems.clear();
    this.startDate = dates.startDate;
    this.endDate = dates.endDate;
    this.resetPayappItems();
  }


  checkPayappNameTruncation(): void {
    setTimeout(() => {
      const el = this.payappNameEl?.nativeElement;
      this.isPayappNameTruncated = el ? el.scrollWidth > el.clientWidth : false;
    });
  }

  onPayappChange(selectedPayapp): void {

    this.showOutstanding = false;
    this.showDeferredItems = false;
    this.showPartialPaymentReview = false;
    this.quantityDrafts = {};
    this.payableQuantityMap = {};
    this.includedPriorItems.clear();
    this.selectedPayapp = selectedPayapp;
    this.checkPayappNameTruncation();
    this.updateRetainagePercentage();
    this.readOnly = this.selectedPayapp?.id !== 'unpaid';
    // Show G703 view for submitted pay apps (unless legacy layout is enabled)
    const useLegacyLayout = this.billingSettings?.use_legacy_payapp_layout === true;
    this.showG703View = this.readOnly && !useLegacyLayout;
    const showStatus = !this.readOnly && this.canEdit;
    this.displayedColumns = ['Date', 'Site', 'Created By', 'Bid Item', 'Quantity', 'Amount', ...(showStatus ? ['Status'] : [])];
    this.sortByColumns = this.showG703View
      ? this.g703SortByColumns
      : ['Date', 'Site', 'Bid Item', ...(showStatus ? ['Status'] : []), 'Quantity', 'Amount', 'Created By'];
    this.selectedSortColumn = null; // Reset sort when switching views

    this.store.select(selectPayappItems).pipe(
      take(1),
    ).subscribe(items => {
      const existingItems = items[selectedPayapp?.id];
      if (existingItems) {
        this.payappItems = existingItems;
        this.initializePayappItems(true);
      } else {
        this.fetchPayappItems(this.selectedPayapp?.id);
      }
    });
    // reset date filters when switching payapps
    this.onDateChanged({ startDate: null, endDate: null });
  }

  resetPayappItems(): void {
    // The main subscription handles subsequent server updates.
    this.store.select(selectPayappItems).pipe(
      take(1),
      filter((items: any) => !!items && Object.keys(items).length > 0),
      takeUntil(this.onDestroy),
    ).subscribe((items: any) => {

      const prevPayappItems = this.payappItems;
      this.allUnpaidItems = items.unpaid || [];
      this.payappItems = items[this.selectedPayapp?.id] || [];

      this.payappItems = this.applyFilters(this.payappItems);

      const resetPagination = !(this.payappItems && this.payappItems.length === prevPayappItems?.length);
      this.initializePayappItems(resetPagination);
    });

  }

  applyFilters = (items) => items.filter(item => {
    // Explicit inclusion from the backlog is a payment selection, even if an
    // existing date/status/site filter would otherwise hide that source row.
    if (this.selectedPayapp?.id === 'unpaid' && this.includedPriorItems.has(item.report_bid_item_id)) return true;
    const reportDate = moment(item.report_date);
    const dateMatch = (!this.startDate || reportDate.isSameOrAfter(this.startDate, 'day')) &&
      (!this.endDate || reportDate.isSameOrBefore(this.endDate, 'day'));

    if (!dateMatch) {
      return false;
    }

    if (this.filters) {
      const { status, contractorStatus, site, creator } = this.filters;

      // Filter by status
      const statusMatch = !status?.length || status.includes(item.pay_app_status ?? 'ready')
        || (status.includes('deferred') && this.deferredQuantity(item) > 0);

      // Filter by contractor status
      const contractorStatusMatch = !contractorStatus?.length || contractorStatus.includes(item.contractor_status ?? 'agree');

      // Filter by site
      const siteMatch = !site?.length || site.includes(item.site_id);

      // Filter by creator
      const creatorMatch = !creator?.length || creator.includes(item.created_by_id);

      // Return true if the item matches all filters
      return statusMatch && contractorStatusMatch && siteMatch && creatorMatch;
    }

    return true;
  })


  onFiltersChanged(filters): void {
    this.filters = filters;
    this.resetPayappItems();
  }

  onSummaryExpandedChange(expanded: boolean): void {
    this.summaryExpanded = expanded;
    localStorage.setItem('payapp-summary-expanded', String(expanded));
  }

  confirmDeletePayapp(): void {

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Recall payapp',
        `Are you sure you want to recall this payapp?`,
        'Recall',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      // Dispatch the delete action here
      this.store.dispatch(DeletePayappRequest({
        payload: {
          projectId: this.projectDetails?.id,
          payappId: this.selectedPayapp.id,
        },
      }));

      this.analyticsService.capture('payapp_action', {
        action: 'recalled',
        project_id: this.projectDetails?.id,
      });
    });
  }

  calculateTotalAmount(): void {
    const selectedByBid = new Map<number, number>();
    this.contractOverages.clear();
    this.contractPayableQuantities.clear();
    this.partialPaymentReviewCount = 0;
    this.fullDeferralReviewCount = 0;
    this.hasContractOverage = false;
    if (!this.readOnly) {
      this.payappItems?.forEach(item => {
        if (this.statusMap[item.report_bid_item_id] === 'ready') {
          selectedByBid.set(item.bid_item_key, (selectedByBid.get(item.bid_item_key) || 0) + this.payableQuantity(item));
        }
      });
      this.payappItems?.forEach(item => {
        const selected = selectedByBid.get(item.bid_item_key) || 0;
        const overage = selected > 0 && item.adjusted_quantity != null
          ? Math.max(0, Math.round((selected + Number(item.quantity_previous || 0) - item.adjusted_quantity) * 1e8) / 1e8) : 0;
        this.contractOverages.set(item.bid_item_key, overage);
        this.hasContractOverage ||= overage > 0;
      });
      // Suggest deferring the newest entries first, regardless of table sort or page.
      // These are previews only: generation still requires saving each split and reason.
      const excessByBid = new Map(this.contractOverages);
      [...(this.payappItems || [])].sort((a, b) =>
        String(b.report_date).localeCompare(String(a.report_date))
        || b.report_bid_item_id.localeCompare(a.report_bid_item_id)).forEach(item => {
        const ready = this.statusMap[item.report_bid_item_id] === 'ready';
        const requested = ready ? this.payableQuantity(item) : 0;
        const excess = excessByBid.get(item.bid_item_key) || 0;
        const reduction = Math.min(Math.max(0, requested), excess);
        this.contractPayableQuantities.set(item.report_bid_item_id,
          Math.round((requested - reduction) * 1e8) / 1e8);
        excessByBid.set(item.bid_item_key, Math.round((excess - reduction) * 1e8) / 1e8);
        if (this.needsPartialPaymentReview(item)) {
          this.partialPaymentReviewCount++;
          if (this.contractPayableQuantity(item) <= 0) this.fullDeferralReviewCount++;
        }
      });
    }
    this.totalAmount = this.payappItems?.reduce((total, item) =>
      (this.statusMap[item.report_bid_item_id] === 'ready' ||
        this.statusMap[item.report_bid_item_id] === 'paid')
        ? total + ((this.readOnly ? this.payableQuantity(item) : this.contractPayableQuantity(item)) * item.unit_price)
        : total, 0);
    if (this.showPartialPaymentReview || this.showDeferredItems) this.filterDisplayedItems();
  }

  filterDisplayedItems(): void {
    if (!this.payappItems) {
      return;
    }
    if (this.showPartialPaymentReview && !this.readOnly) {
      // Review every unresolved split together, without changing the payapp selection.
      this.displayedItems = this.sortItems(this.payappItems).filter(item => this.needsPartialPaymentReview(item));
      return;
    }
    const searchTermLower = this.searchControl.value?.toLowerCase() || '';
    const visibleItems = this.sortItems(this.showDeferredItems ? this.deferredItems : this.showOutstanding ? this.priorPeriodItems : this.payappItems)
      .filter(item => {
        const transformedItem = {
          ...item,
          pay_app_status: item.pay_app_status === null ? 'Ready' : item.pay_app_status,
          report_date: moment(item.report_date).format('MMM D, YYYY'),
        };

        return Object.values(transformedItem).some(val =>
          String(val).toLowerCase().includes(searchTermLower),
        );
      });
    this.pagination.length = visibleItems.length;
    this.displayedItems = visibleItems.slice(this.pagination.pageIndex * this.pagination.pageSize,
        (this.pagination.pageIndex + 1) * this.pagination.pageSize);

    // Update grouped items for G703 view
    if (this.showG703View) this.groupItemsByBidItem();
  }

  pageChanged(page: number) {
    if (!this.initialized) {
      return;
    }
    this.pagination.pageIndex = page - 1;
    this.filterDisplayedItems();
  }

  async generatePayapp(): Promise<void> {
    if (this.hasContractOverage) {
      this.commonService.notification('Defer quantities above the approved contract before generating the pay app', 'error');
      return;
    }
    // Calculate items breakdown
    const readyItems = this.payappItems.filter(item => this.statusMap[item.report_bid_item_id] === 'ready');
    const deferredItems = this.payappItems.filter(item => this.statusMap[item.report_bid_item_id] === 'deferred');
    const partiallyDeferredItems = readyItems.filter(item => this.payableQuantity(item) < item.quantity);
    const rejectedItems = this.payappItems.filter(item => this.statusMap[item.report_bid_item_id] === 'rejected');

    const calcTotal = (items: PayappItem[]) => items.reduce((sum, item) => sum + (this.payableQuantity(item) * item.unit_price), 0);
    const installedByItemCode = readyItems.reduce<Record<string, number>>((acc, item) => {
      acc[item.item] = (acc[item.item] || 0) + ((this.payableQuantity(item) || 0) * (item.unit_price || 0));
      return acc;
    }, {});
    let mohBalances: MohBalance[] = [];

    if (this.projectDetails?.enable_moh_per_item) {
      try {
        const response = await this.projectsService.fetchMohBalances(this.projectDetails.id).toPromise();
        mohBalances = response?.balances || response?.data?.balances || [];
      } catch {
        this.commonService.notification('Unable to load material on hand balances', 'error');
        return;
      }
    }

    const dialogData: CreatePayAppDialogData = {
      itemCount: this.payappItems.length,
      startDate: this.startDate,
      endDate: this.endDate,
      totalAmount: this.totalAmount,
      payappName: '',
      readyItems: { count: readyItems.length, total: calcTotal(readyItems) },
      deferredItems: {
        count: deferredItems.length + partiallyDeferredItems.length,
        total: deferredItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0)
          + partiallyDeferredItems.reduce((sum, item) => sum + (item.quantity - this.payableQuantity(item)) * item.unit_price, 0),
      },
      rejectedItems: { count: rejectedItems.length, total: calcTotal(rejectedItems) },
      billingSettings: this.billingSettings ? {
        retainage_percentage: this.billingSettings.retainage_percentage ?? 5,
        liquidated_damages_per_day: this.billingSettings.liquidated_damages_per_day ?? 0,
        current_retainage_held: 0, // TODO: Calculate from previous pay apps
        days_past_contract_end: undefined,
        days_remaining: undefined,
        default_template_id: this.billingSettings.default_template_id,
      } : undefined,
      useLegacyLayout: this.useLegacyLayout,
      enableMohPerItem: this.projectDetails?.enable_moh_per_item === true,
      mohBalances,
      installedByItemCode,
      validateBeforeClose: async (result: CreatePayAppDialogResult) => {
        const itemMaps = {
          quantity: this.payableQuantityMap,
          status: this.statusMap,
          comments: this.commentsMap,
        };
        const payloadData = PayappItem.toPayload(this.payappItems, itemMaps, result.payappName);
        const payload = {
          projectId: this.projectDetails.id,
          ...payloadData,
          payappName: result.payappName,
          start_date: this.startDate || null,
          end_date: this.endDate || null,
          retainage_released: result.retainage_released,
          liquidated_damages_days: result.liquidated_damages_days,
          material_on_hand: result.material_on_hand,
          moh: result.moh,
          template_id: this.billingSettings?.default_template_id,
        };

        try {
          const validation = await this.projectsService.validatePayapp(this.projectDetails.id, payload).toPromise();
          if (validation?.blocks?.length) {
            return { blocks: validation.blocks };
          }
          if (validation?.warnings?.length) {
            const acknowledged = [];
            for (const warning of validation.warnings) {
              const comment = await this.requestMohWarningAcknowledgement(warning);
              if (!comment) {
                return { errorMessage: 'Material on hand warning acknowledgement is required' };
              }
              acknowledged.push({
                code: warning.code,
                bid_item_id: warning.bid_item_id,
                comment,
              });
            }
            return { acknowledged_warnings: acknowledged };
          }
          return {};
        } catch {
          return { errorMessage: 'Unable to validate material on hand entries' };
        }
      },
    };

    const dialogRef = this.dialog.open(CreatePayappDialogComponent, {
      width: this.projectDetails?.enable_moh_per_item ? '920px' : '520px',
      maxHeight: '90vh',
      data: dialogData,
    });

    dialogRef.afterClosed().pipe(
      filter((result: CreatePayAppDialogResult) => result !== undefined),
      tap(async (result: CreatePayAppDialogResult) => {
        const itemMaps = {
          quantity: this.payableQuantityMap,
          status: this.statusMap,
          comments: this.commentsMap,
        };
        const payloadData = PayappItem.toPayload(this.payappItems, itemMaps, result.payappName);
        const payload = {
          projectId: this.projectDetails.id,
          ...payloadData,
          payappName: result.payappName,
          start_date: this.startDate || null,
          end_date: this.endDate || null,
          // New fields from enhanced dialog
          retainage_released: result.retainage_released,
          liquidated_damages_days: result.liquidated_damages_days,
          material_on_hand: result.material_on_hand,
          moh: result.moh,
          template_id: this.billingSettings?.default_template_id,
          acknowledged_warnings: result.acknowledged_warnings,
        };

        this.store.dispatch(GeneratePayappRequest({
          payload: {
            projectId: this.projectDetails?.id,
            data: payload,
          },
        }));

        this.analyticsService.capture('payapp_action', {
          action: 'generated',
          project_id: this.projectDetails?.id,
        });
      }),
    ).subscribe();
  }

  async requestMohWarningAcknowledgement(warning): Promise<string | null> {
    const dialogRef = this.dialog.open(MohWarningAckDialogComponent, {
      disableClose: true,
      data: {
        message: warning.message,
        itemCode: warning.item_code,
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  canEditMohAttachments(): boolean {
    return this.canEdit && this.readOnly && !this.isContractorMode && this.selectedPayapp?.id !== 'unpaid';
  }

  getMohAttachments(group): any[] {
    return group?.moh?.attachments || [];
  }

  getMohAttachmentName(attachment): string {
    return attachment?.filename || attachment?.original_name || attachment?.name || 'Attachment';
  }

  getMohAttachmentUrl(attachment): string {
    return attachment?.download_url || attachment?.original_url || attachment?.preview_url || attachment?.thumb_url;
  }

  getMohAttachmentPreview(attachment): string {
    return attachment?.thumb_url || attachment?.preview_url || attachment?.original_url || '/assets/icons/file.png';
  }

  isMohAttachmentImage(attachment): boolean {
    return /^image\//.test(attachment?.mime || '');
  }

  /**
   * True when the attachment has a server-rendered thumbnail/preview we can
   * display as an `<img>`. Covers images directly plus PDFs (which the
   * AttachmentService rasterizes to a JPEG thumbnail on upload). Everything
   * else falls back to the document icon.
   */
  hasMohAttachmentThumb(attachment): boolean {
    return !!(attachment?.thumb_url || attachment?.preview_url);
  }

  onMohAttachmentSelected(event: Event, group): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length || !group?.moh?.uuid || !this.projectDetails?.id || !this.selectedPayapp?.id) {
      input.value = '';
      return;
    }

    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('inputFiles', file));
    this.mohAttachmentUploading[group.moh.uuid] = true;

    this.projectsService.uploadMohAttachment(
      this.projectDetails.id,
      this.selectedPayapp.id,
      group.moh.uuid,
      formData,
    ).subscribe({
      next: (response) => {
        const attachments = Array.isArray(response?.data) ? response.data : response?.data ? [response.data] : [];
        this.addMohAttachments(group.moh.uuid, attachments.map(attachment => this.normalizeMohAttachment(attachment)));
        this.commonService.notification('Material attachment uploaded', 'success', undefined, 3000);
      },
      error: () => {
        this.commonService.notification('Unable to upload material attachment', 'error');
      },
      complete: () => {
        this.mohAttachmentUploading[group.moh.uuid] = false;
        input.value = '';
      },
    });
  }

  deleteMohAttachment(group, attachment): void {
    const attachmentId = attachment?.uuid || attachment?.id;
    if (!attachmentId || !group?.moh?.uuid || !this.projectDetails?.id || !this.selectedPayapp?.id) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete attachment?',
        `Are you sure you want to delete "${this.getMohAttachmentName(attachment)}"?`,
        'Delete',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.projectsService.deleteMohAttachment(
        this.projectDetails.id,
        this.selectedPayapp.id,
        group.moh.uuid,
        attachmentId,
      ).subscribe({
        next: () => {
          this.removeMohAttachment(group.moh.uuid, attachmentId);
          this.commonService.notification('Material attachment deleted', 'success', undefined, 3000);
        },
        error: () => {
          this.commonService.notification('Unable to delete material attachment', 'error');
        },
      });
    });
  }

  private normalizeMohAttachment(attachment): any {
    return {
      uuid: attachment?.uuid || attachment?.id,
      filename: attachment?.filename || attachment?.original_name || attachment?.name,
      content_type: attachment?.content_type || attachment?.mime,
      size: attachment?.size,
      uploaded_at: attachment?.uploaded_at || attachment?.created_at,
      download_url: attachment?.download_url || attachment?.original_url,
      // Pass through thumb/preview URLs so we render the rasterized PDF
      // first-page (or image thumb) immediately after upload instead of
      // falling back to the document icon.
      thumb_url: attachment?.thumb_url,
      preview_url: attachment?.preview_url,
    };
  }

  private addMohAttachments(mohUuid: string, attachments: any[]): void {
    this.updateMohAttachments(mohUuid, current => [...current, ...attachments]);
  }

  private removeMohAttachment(mohUuid: string, attachmentId: string): void {
    this.updateMohAttachments(mohUuid, current => current.filter(attachment => (attachment?.uuid || attachment?.id) !== attachmentId));
  }

  private updateMohAttachments(mohUuid: string, updater: (attachments: any[]) => any[]): void {
    this.groupedItems = this.groupedItems.map(group => {
      if (group?.moh?.uuid !== mohUuid) {
        return group;
      }
      return {
        ...group,
        moh: {
          ...group.moh,
          attachments: updater(group.moh.attachments || []),
        },
      };
    });
    this.displayedGroupedItems = this.groupedItems.slice(
      this.pagination.pageIndex * this.pagination.pageSize,
      (this.pagination.pageIndex + 1) * this.pagination.pageSize,
    );

  }

  downloadPayapp(format: string = 'pdf'): void {
    if (!this.selectedPayapp || this.selectedPayapp.id === 'unpaid' || !this.projectDetails) {
      return;
    }

    this.downloading = true;
    const templateId = this.selectedPayapp.template_id || this.billingSettings?.default_template_id;
    this.projectsService.downloadPayapp(
      this.projectDetails.id,
      this.selectedPayapp.id,
      this.selectedPayapp.name,
      templateId,
      format
    ).subscribe(
      () => {
        this.downloading = false;
        const label = format === 'excel' ? 'Excel' : 'PDF';
        this.snackBar.open(`${label} downloaded successfully`, '', { duration: 3000 });
      },
      (error) => {
        this.downloading = false;
        const label = format === 'excel' ? 'Excel' : 'PDF';
        this.snackBar.open(`Failed to download ${label}`, '', { duration: 5000 });
        console.error('Error downloading payapp:', error);
      }
    );
  }

  downloadMohLedger(): void {
    if (!this.projectDetails?.id || this.downloadingMohLedger) {
      return;
    }

    this.downloadingMohLedger = true;
    this.projectsService.downloadMohLedger(
      this.projectDetails.id,
      this.projectDetails.name
    ).subscribe(
      () => {
        this.downloadingMohLedger = false;
        this.snackBar.open('Material on hand ledger downloaded successfully', '', { duration: 3000 });
      },
      (error) => {
        this.downloadingMohLedger = false;
        this.snackBar.open('Failed to download material on hand ledger', '', { duration: 5000 });
        console.error('Error downloading MOH ledger:', error);
      }
    );
  }

  getPayappTooltip(payapp: any): string {
    const name = payapp.name || (payapp.estimate_number ? 'Pay Estimate' : payapp.id === 'unpaid' ? 'Unpaid' : '');
    const parts: string[] = [name];

    if (payapp.estimate_number) {
      parts[0] = `${name} #${payapp.estimate_number}`;
    }

    if (payapp.start_date && payapp.end_date) {
      const startDate = new Date(payapp.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const endDate = new Date(payapp.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      parts.push(`(${startDate} - ${endDate})`);
    }

    if (payapp.calculated?.amount_due_this_period) {
      const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(payapp.calculated.amount_due_this_period);
      parts.push(`- ${amount}`);
    }

    return parts.join(' ');
  }

  // Compare payapps by ID for mat-select binding (object references differ after store updates)
  comparePayapps = (p1: any, p2: any): boolean => {
    return p1 && p2 && p1.id === p2.id;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
